import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ZintModalConfig } from '../../../classes/types';
import { publicMethods } from '../../../globals';
import { Subscription, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'wizard-crm-auth',
    templateUrl: './wizard-crm-auth.component.html',
    styleUrl: './wizard-crm-auth.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WizardCrmAuthComponent {
  @Input() stepData: Record<string, any>;
  @Output() setStepIsReady = new EventEmitter<boolean>();

  ACTION_TYPE = 'get_auth_url';

  crmType: string;
  requiredCheckboxChecked: boolean;
  authURL: string;

  pollingSub: Subscription;

  allowReauthorisation = false;
  showTermsModal = false;
  showDataUsageModal = false;
  termsModalConfig: ZintModalConfig;
  dataUsageModalConfig: ZintModalConfig;

  constructor(
    public crmService: CRMIntegrationsService,
    public pubMethods: publicMethods,
    private snackbar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    // run effect and emit if step is for non-su
    effect(() => {
      const isAuthReady =
        this.crmService.wizardStepsReadyState()[this.stepData.stepType];
      if (isAuthReady && this.stepData.superuserOnly === false) {
        this.setStepIsReady.emit(isAuthReady);
      }
    });
  }

  ngOnInit(): void {
    this.crmType = this.crmService.configCRMType();
    this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
  }

  ngAfterViewInit(): void {
    this.requiredCheckboxChecked = this.stepData.isReady;
    this.allowReauthorisation = this.stepData.isReady;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.stopPoll();
  }

  startPoll(): void {
    this.pollingSub = timer(3000, 2500)
      .pipe(
        switchMap(() => {
          // switchMap cancels previous requests if a new one is triggered before the last one is complete.
          return this.crmService.getWizardStepStatus(this.stepData.stepType);
        }),
        tap(data => {
          if (data?.isReady) {
            this.stopPoll();
            this.crmService.wizardStepsReadyState.update(state => {
              return { ...state, [this.stepData.stepType]: data.isReady };
            });
          }
        })
      )
      .subscribe();
  }

  stopPoll(): void {
    this.pollingSub?.unsubscribe();
  }

  getStepActionContent(): void {
    this.crmService
      .getCRMStepActionContent({
        stepType: this.stepData.stepType,
        actionType: this.ACTION_TYPE,
      })
      .subscribe(data => {
        if (data) {
          this.authURL = data.url;
        }
      });
  }

  authAction(): void {
    if (this.authURL) {
      this.startPoll();
      window.open(this.authURL, '_blank');
    } else {
      this.reAuthAction();
    }
  }

  reAuthAction(): void {
    // Request a new AuthURL then open it immediately
    this.crmService
      .getCRMStepActionContent({
        stepType: this.stepData.stepType,
        actionType: this.ACTION_TYPE,
      })
      .subscribe(data => {
        if (data) {
          this.authURL = data.url;
          if (this.authURL) {
            window.open(this.authURL, '_blank');
          } else {
            this.snackbar.open(
              'We were not able to initiate authorisation. Please refresh the page and try again',
              'X',
              {
                duration: 3100,
              }
            );
          }
        }
      });
  }

  isTnCCheckboxDisabled(): boolean {
    const { isCompleted, isReady, superuserOnly } = this.stepData;
    return superuserOnly ? isCompleted : isReady;
  }

  openTermsModal(): void {
    this.termsModalConfig = {
      showHeaderRow: false,
      size: 'xl',
      name: 'crmTerms',
    };

    this.showTermsModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(this.termsModalConfig.name);
  }

  openDataUseModal(): void {
    this.dataUsageModalConfig = {
      showHeaderRow: false,
      size: 'xl',
      name: 'dataUsage',
    };

    this.showDataUsageModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(this.dataUsageModalConfig.name);
  }
}
